import type { PostgreSQLCDCDiscovery } from '../../api/types';

export type PostgreSQLCDCGuideProvider = PostgreSQLCDCDiscovery['provider'] | 'docker';

export const POSTGRESQL_CDC_PROVIDER_NAMES: Record<PostgreSQLCDCGuideProvider, string> = {
  docker: 'Docker',
  self_managed: 'Self-managed PostgreSQL',
  aws: 'Amazon RDS for PostgreSQL',
  azure: 'Azure Database for PostgreSQL Flexible Server',
  gcp: 'Google Cloud SQL for PostgreSQL',
};

export const POSTGRESQL_CDC_DOCKER_RUN_COMMAND =
  'postgres -c wal_level=logical -c max_replication_slots=10 -c max_wal_senders=10';

export const POSTGRESQL_CDC_DOCKER_COMPOSE_COMMAND = `command:
  - postgres
  - -c
  - wal_level=logical
  - -c
  - max_replication_slots=10
  - -c
  - max_wal_senders=10`;

function identifier(value: string): string {
  return `"${value.split('"').join('""')}"`;
}

const PROVIDER_STEPS: Record<PostgreSQLCDCGuideProvider, string[]> = {
  docker: [
    'Append the Docker Run command shown below to the existing docker run options. Preserve the existing image, environment, volume, network, and port arguments.',
    'For Docker Compose, add the command block shown below to the PostgreSQL service, then run docker compose up -d --force-recreate. Restarting an unchanged container is not sufficient.',
    'After recreation, confirm wal_level is logical before running the SQL below.',
  ],
  azure: [
    'In Azure Portal, open the Flexible Server parameters page, set wal_level to logical, and save the change.',
    'Restart the Flexible Server if Azure reports the parameter as pending restart, then confirm wal_level is logical before running the SQL below.',
  ],
  aws: [
    'In the attached RDS parameter group, set the static rds.logical_replication parameter to 1.',
    'Apply the parameter group, reboot the DB instance, and confirm wal_level is logical before running the SQL below.',
  ],
  gcp: [
    'In Cloud SQL, enable the cloudsql.logical_decoding database flag.',
    'Apply the flag, complete any restart requested by Cloud SQL, and confirm wal_level is logical before running the SQL below.',
  ],
  self_managed: [
    'Persist wal_level = logical in the active postgresql.conf or PostgreSQL startup arguments.',
    'Ensure max_replication_slots and max_wal_senders have capacity, restart PostgreSQL, and confirm wal_level is logical. Restarting without changing the active configuration is not sufficient.',
  ],
};

const PROVIDER_DISABLE_STEPS: Record<PostgreSQLCDCGuideProvider, string[]> = {
  docker: [
    'Only after all logical replication consumers and slots on this PostgreSQL server are removed, recreate the container without the wal_level, max_replication_slots, and max_wal_senders command overrides added for CDC.',
  ],
  self_managed: [
    'Only after all logical replication consumers and slots on this PostgreSQL server are removed, restore the previous postgresql.conf or startup settings and restart PostgreSQL.',
  ],
  azure: [
    'Only after all logical replication consumers and slots on the server are removed, restore wal_level on the Flexible Server parameters page and complete any requested restart.',
  ],
  aws: [
    'Only after all logical replication consumers and slots on the instance are removed, set rds.logical_replication to 0 in the attached parameter group, apply it, and reboot if required.',
  ],
  gcp: [
    'Only after all logical replication consumers and slots on the instance are removed, disable the cloudsql.logical_decoding flag and complete any restart requested by Cloud SQL.',
  ],
};

export function postgreSQLCDCProviderSteps(provider: PostgreSQLCDCGuideProvider): string[] {
  return PROVIDER_STEPS[provider];
}

export function postgreSQLCDCProviderDisableSteps(provider: PostgreSQLCDCGuideProvider): string[] {
  return PROVIDER_DISABLE_STEPS[provider];
}

export interface PostgreSQLCDCVerificationFeedback {
  error: string | null;
}

export function postgreSQLCDCVerificationFeedback(
  discovery: PostgreSQLCDCDiscovery | undefined,
): PostgreSQLCDCVerificationFeedback {
  if (!discovery) {
    return {
      error: 'Verification completed, but PostgreSQL CDC details were not returned. Retry verification or contact support.',
    };
  }
  return { error: null };
}

/** Produce administrator SQL containing no host, password, or connection string. */
export function generatePostgreSQLCDCAdminScript(
  discovery: PostgreSQLCDCDiscovery,
  provider: PostgreSQLCDCGuideProvider = discovery.provider,
): string {
  const role = identifier(discovery.current_user);
  const permission = provider === 'aws'
    ? `GRANT rds_replication TO ${role};`
    : `ALTER ROLE ${role} WITH REPLICATION;`;
  const permissionVerification = provider === 'aws'
    ? `SELECT pg_has_role(${sqlLiteral(discovery.current_user)}, 'rds_replication', 'member') AS has_replication_access;`
    : `SELECT rolname, rolreplication FROM pg_roles WHERE rolname = ${sqlLiteral(discovery.current_user)};`;
  return [
    '-- PostgreSQL CDC prerequisites for the existing connection role.',
    '-- First complete the hosting-provider steps shown in the setup guide.',
    '-- Run as a database administrator only after SELECT current_setting(\'wal_level\') returns logical.',
    permission,
    '',
    '-- Verification (the application also checks these values):',
    "SELECT current_setting('wal_level') AS wal_level;",
    permissionVerification,
    '',
    '-- Replication slots retain WAL. Monitor lag and remove unused slots deliberately.',
    'SELECT slot_name, active, restart_lsn, confirmed_flush_lsn FROM pg_replication_slots;',
    '',
  ].join('\n');
}

/** Remove only resources the saved connection records as application-created. */
export function generatePostgreSQLCDCCleanupScript(discovery: PostgreSQLCDCDiscovery): string {
  const configured = discovery.configured;
  const lines = [
    '-- Stop and remove the CDC connection in the application before running this script.',
    '-- This script removes only resources recorded as created for that connection.',
  ];

  if (configured?.create_publication) {
    lines.push(`DROP PUBLICATION IF EXISTS ${identifier(configured.publication_name)};`);
  } else {
    lines.push('-- No application-created publication is recorded; no publication will be dropped.');
  }

  if (configured?.create_slot) {
    lines.push(
      '-- An active slot is deliberately not dropped. Stop its consumer, confirm active = false, and rerun.',
      `SELECT pg_drop_replication_slot(${sqlLiteral(configured.slot_name)})`,
      'WHERE EXISTS (',
      '  SELECT 1 FROM pg_replication_slots',
      `  WHERE slot_name = ${sqlLiteral(configured.slot_name)} AND NOT active`,
      ');',
    );
  } else {
    lines.push('-- No application-created slot is recorded; no replication slot will be dropped.');
  }

  lines.push(
    '',
    '-- Role replication access is not revoked automatically because it may be shared.',
    '-- Do not disable logical replication server-wide until every consumer and slot is removed.',
    'SELECT slot_name, active, database FROM pg_replication_slots;',
    '',
  );
  return lines.join('\n');
}

function sqlLiteral(value: string): string {
  return `'${value.split("'").join("''")}'`;
}